
// component-fit-day-events.js
class FitDayEvents extends HTMLElement {
    /** @type {(slide: HTMLElement) => void} */
    inViewCallback = null;

    // 记录已触发的轮播图索引
    prevCallbackIndexList = new Set();

    constructor() {
        super();
        this.swiper = null;
        this.tabs = [];
    }

    connectedCallback() {
        this.init();
    }

    disconnectedCallback() {
        this.cleanup();
    }

    /**
     * @param {(slide: HTMLElement) => void} callback 
     */
    setInViewCallback(callback) {
        this.inViewCallback = callback;
    }

    init() {
        this.initElements();
        this.initSwiper();
        this.bindEvents();
    }

    initElements() {
        // 获取内部元素
        this.tabContainer = this.querySelector('.fit-day-events__tabs');
        this.tabs = Array.from(this.querySelectorAll('.fit-day-events__tab'));
        this.sliderWrapper = this.querySelector('.fit-day-events__slider');

        // 获取配置
        this.autoPlay = this.dataset.autoPlay === 'true';
        const delayRaw = parseInt(this.dataset.autoPlayDelay, 10);
        const delayMs = Number.isFinite(delayRaw) && delayRaw > 0 ? delayRaw : 5;
        this.autoPlayDelay = delayMs >= 100 ? delayMs : delayMs * 1000;
    }

    initSwiper() {
        if (!this.sliderWrapper) {
            return;
        }

        // 检查 Swiper 是否已加载
        if (typeof Swiper === 'undefined') {
            console.error('Swiper.js is not loaded');

            // 尝试延迟加载
            setTimeout(() => {
                if (typeof Swiper !== 'undefined') {
                    this.initSwiper();
                } else {
                    console.error('Swiper.js still not loaded after delay');
                }
            }, 100);
            return;
        }

        // 如果 Swiper 已存在，先销毁
        if (this.swiper) {
            this.swiper.destroy(true, true);
        }

        const swiperOptions = {
            slidesPerView: 'auto',
            loop: true,
            speed: 500,
            resizeObserver: true,
            observeParents: true,
            centeredSlides: true,
            navigation: {
                nextEl: this.querySelector('.fit-day-events__nav--next'),
                prevEl: this.querySelector('.fit-day-events__nav--prev'),
            },
            pagination: {
                el: this.querySelector('.fit-day-events__pagination'),
                clickable: true,
            },
            on: {
                init: this.onSwiperInit.bind(this),
                slideChange: this.onSlideChange.bind(this),
                slideChangeTransitionEnd: this.onSlideChangeEnd.bind(this),
                resize: this.onSwiperResize.bind(this),
            }
        };

        // 添加自动播放配置
        if (this.autoPlay) {
            swiperOptions.autoplay = {
                delay: this.autoPlayDelay,
                disableOnInteraction: false,
                pauseOnMouseEnter: true,
            };
        }

        try {
            this.swiper = new Swiper(this.sliderWrapper, swiperOptions);
            console.log('Swiper initialized successfully');
        } catch (error) {
            console.error('Failed to initialize Swiper:', error);
        }
    }

    bindEvents() {
        if (this.tabs.length > 0) {
            this.tabs.forEach((tab, index) => {
                tab.addEventListener('click', (e) => {
                    e.preventDefault();
                    if (this.swiper) {
                        this.swiper.slideToLoop(index);
                        this.updateTabs(index);
                    }
                });
            });
        }
        this.querySelectorAll(".fit-day-events__subscribe-btn").forEach(btn => {
            btn.addEventListener('click', (e) => {
                window.dataLayer.push({ "event_parameters": null });
                window.dataLayer.push({
                    "event": "ga4Event",
                    "event_name": "select_content",
                    "event_parameters": {
                        "content_type": "section",
                        "content_name": "section_join",
                        "button_name": "Register"
                    }
                });
            });
        });
        this.inViewObserver = createObserverToListenElementInViewport(this.handleInViewCallback.bind(this));
        this.inViewObserver.observe(this.sliderWrapper);
    }

    /**
     * 轮播图滚动进视图触发
     */
    handleInViewCallback() {
        if (this.inViewCallback && !this.prevCallbackIndexList.has(this.swiper.realIndex)) {
            // 添加到记录 
            this.prevCallbackIndexList.add(this.swiper.realIndex);
            // 触发回调
            this.inViewCallback(this.swiper.slides[this.swiper.activeIndex]);
        }
    }

    onSwiperInit() {
        // 初始化后更新 Tab 状态
        if (this.swiper) {
            this.updateTabs(this.swiper.realIndex);

            // 确保初始 Tab 在移动端可见
            if (this.isMobile && this.tabs.length > 0) {
                const activeTab = this.tabs[this.swiper.realIndex];
                if (activeTab) {
                    this.scrollTabIntoView(activeTab);
                }
            }
        }
    }

    onSlideChange() {
        // 幻灯片切换时更新 Tab
        if (this.swiper) {
            this.updateTabs(this.swiper.realIndex);

            // 如果轮播图完全在视图内，则手动触发一次 InView
            if (isElementInViewportWithCondition(this.sliderWrapper)) {
                this.handleInViewCallback();
            }
        }
    }

    onSlideChangeEnd() {
        // 幻灯片切换结束后，确保 Tab 在移动端可见
        if (this.isMobile && this.swiper && this.tabs.length > 0) {
            const activeTab = this.tabs[this.swiper.realIndex];
            if (activeTab) {
                this.scrollTabIntoView(activeTab);
            }
        }
    }

    onSwiperResize() {
        this.isMobile = window.innerWidth <= 768;
    }

    updateTabs(activeIndex) {
        if (!this.tabs.length) {
            return;
        }
        this.tabs.forEach((tab, index) => {
            const isActive = index === activeIndex;

            tab.classList.toggle('fit-day-events__tab--active', isActive);
            tab.setAttribute('aria-selected', isActive);
            tab.setAttribute('tabindex', isActive ? '0' : '-1');
        });
    }


    scrollTabIntoView(tab) {
        if (!this.tabContainer || !tab || !this.tabs.length) return;

        const containerWidth = this.tabContainer.offsetWidth;
        const tabLeft = tab.offsetLeft;
        const tabWidth = tab.offsetWidth;

        // 计算滚动位置使 Tab 居中
        const scrollLeft = tabLeft - (containerWidth / 2) + (tabWidth / 2);

        this.tabContainer.scrollTo({
            left: scrollLeft,
            behavior: 'smooth'
        });
    }

    reinitSwiper() {
        this.initSwiper();
    }

    cleanup() {
        // 清理 Swiper 实例
        if (this.swiper) {
            this.swiper.destroy(true, true);
            this.swiper = null;
        }
        this.inViewObserver?.disconnect();
    }

    // 公共 API 方法
    nextSlide() {
        if (this.swiper) {
            this.swiper.slideNext();
        }
    }

    prevSlide() {
        if (this.swiper) {
            this.swiper.slidePrev();
        }
    }

    goToSlide(index) {
        if (this.swiper && index >= 0 && index < this.swiper.slides.length) {
            this.swiper.slideToLoop(index);
        }
    }

    get activeIndex() {
        return this.swiper ? this.swiper.realIndex : 0;
    }

    get slidesCount() {
        return this.swiper ? this.swiper.slides.length : 0;
    }
}

if (!customElements.get('fit-day-events')) {
    customElements.define('fit-day-events', FitDayEvents);
}